import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, startWith, tap } from 'rxjs/operators';
import { CmaApi } from '../_shared/api/cma.api';
import { AppState } from '../app.state';
import { CmaState } from './cma.state';

@Injectable({
  providedIn: 'root',
})
export class CmaService {
  upgradeSnackbar: MatSnackBarRef<any>;
  constructor(
    private cmaApi: CmaApi,
    private cmaState: CmaState,
    private snackbar: MatSnackBar,
    private appState: AppState
  ) { }

  getAllCmaReports(params):Observable<any> {
    if (this.appState.companyInfoValue.alias == 'valuetest') {
      params.reportCategory = 'smartReport'
    }
    this.cmaState.isLoadingReport = true;
    this.cmaState.loadingMessage = 'Loading reports...';
    return this.cmaApi.getAllCmaReports(params).pipe(
      tap((res: any) => {
        if (res && res.data) {
          this.cmaState.allPropertyReportsValue = res.data;
          this.cmaState.cmaReportsListValue = res.data && res.data.paginatedResults ? res.data.paginatedResults : [];
          this.cmaState.filterValValue.currentPageSize = this.cmaState.cmaReportsListValue.length;
        } else {
          this.cmaState.allPropertyReportsValue = null;
          this.cmaState.cmaReportsListValue = [];
          this.cmaState.filterValValue.currentPageSize = 0;
        }
        this.cmaState.isLoadingReport = false;
      }),
      map((res: any) => {
        return res && res.data ? true : false;
      }),
      catchError((err) => {
        this.cmaState.isLoadingReport = false;
        console.error('getReportInfo : ', err);
        return of(false);
      })
    );
  }

  getCmaReportById(id: string | number, type: string): Observable<any> {
    this.cmaState.cmaReportsValue = null;
    if (type != 'silent') {
      this.cmaState.isLoadingReport = true;
      this.cmaState.loadingMessage = type == 'view' ? 'Fetching report...' : type == 'adjChange' ? 'Fetching adjusted value change...' : 'Fetching comparables...';
    }
    return this.cmaApi.getCmaReportById(id).pipe(
      tap((res: any) => {
        this.cmaState.cmaReportsValue = res?.data ?? null;
        this.cmaState.isLoadingReport = false;
      }),
      startWith({ isLoading: true }), // Emits loading state first
      catchError((err) => {
        this.cmaState.cmaReportsValue = null;
        this.cmaState.isLoadingReport = false;
        return of({ error: true, message: 'Failed to fetch report', details: err });
      }),
      finalize(() => { })
    );
  }

  mapToCMAReport(formValue: any): any {
    return {
      reportType: formValue.reportType,
      searchCascade: {
        searchTiers: [
          {
            searchCriteria: {
              radius: {
                type: 'Value',
                value: formValue.radius,
              },
              minSoldComps: {
                type: 'Value',
                value: formValue.minSoldComps,
              },
              maxSoldComps: {
                type: 'Value',
                value: formValue.maxSoldComps,
              },
              minListingComps: {
                type: 'Value',
                value: formValue.minListingComps,
              },
              maxListingComps: {
                type: 'Value',
                value: formValue.maxListingComps,
              },
              monthsBack: {
                type: 'Value',
                value: formValue.monthBack,
              },
              limitToSubjectZip: {
                type: 'Value',
                value: formValue.limitToSubjectZip,
              },
              beds: {
                type: 'ValueRange',
                lowTolerance: formValue.minBed,
                highTolerance: formValue.maxBed,
                ignore: formValue.isBed,
              },
              baths: {
                type: 'ValueRange',
                lowTolerance: formValue.minBath,
                highTolerance: formValue.maxBath,
                ignore: formValue.isBath,
              },
              lotSize: {
                type: 'PercentageRange',
                lowTolerance: formValue.minLotSize,
                highTolerance: formValue.maxLotSize,
                ignore: formValue.isLotSize,
              },
              livingArea: {
                type: 'PercentageRange',
                lowTolerance: formValue.minLivingArea,
                highTolerance: formValue.maxLivingArea,
                ignore: formValue.isLivingArea,
              },
              yearBuilt: {
                type: 'ValueRange',
                lowTolerance: formValue.minAge,
                highTolerance: formValue.maxAge,
                ignore: formValue.isAge,
              },
              stories: {
                type: 'ValueRange',
                lowTolerance: formValue.minStories,
                highTolerance: formValue.maxStories,
                ignore: formValue.isStories,
              },
            },
            similarityWeights: {
              proximity: formValue.proximity,
              livingArea: formValue.livingArea,
              lotSize: formValue.lotSize,
              yearBuilt: formValue.yearBuilt,
              saleDate: formValue.saleDate,
              stories: formValue.stories,
              bedroomsTotal: formValue.bedroomsTotal,
            },
          },
        ],
        valueDetermination: formValue.valueDetermination,
      },
    };
  }

  deleteReport(reportId): Observable<{ isLoading?: boolean; data?: any }> {
    this.cmaState.isLoadingReport = true;
    this.cmaState.loadingMessage = 'Deleting report...';
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.cmaApi
        .deleteReport({ reportId: reportId })
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            this.cmaState.isLoadingReport = false;
            observer.next({ data: res ? res : null });
          },
          (err) => {
            this.cmaState.isLoadingReport = false;
            observer.next({ data: null });
          }
        );
    });
  }

  openSnackbar(upgradeMsg, success = false) {
    this.upgradeSnackbar = this.snackbar.open(upgradeMsg, 'Dismiss', {
      duration: 5000,
      panelClass: [success ? 'snackbar-success' : 'snackbar-error'],
      verticalPosition: 'top',
    });
  }

  shareCmaReport(params): Observable<any> {
    return this.cmaApi.shareCmaReport(params).pipe(
      startWith({ isLoading: true }), // Emits loading state first
      catchError((err) => {
        return of({ error: true, message: 'Failed to fetch report', details: err });
      }),
      finalize(() => { })
    );
  }

  shareCmaReportStatistics(): Observable<any> {
    return this.cmaApi.shareCmaReportStatistics().pipe(
      startWith({ isLoading: true }), // Emits loading state first
      catchError((err) => {
        return of({ error: true, message: 'Failed to fetch shared cma report statistics ', details: err });
      }),
      finalize(() => { })
    );
  }

  addSharedUserId(params): Observable<any> {
    return this.cmaApi.addSharedUserId(params).pipe(
      startWith({ isLoading: true }),
      catchError((err) => {
        return of({ error: true, message: 'Failed to fetch report', details: err });
      }),
      finalize(() => { })
    );
  }

  addSharedUserIdToValutestReport(params): Observable<any> {
    return this.cmaApi.addSharedUserIdInValuetestReport(params).pipe(
      startWith({ isLoading: true }),
      catchError((err) => {
        return of({ error: true, message: 'Failed to fetch report', details: err });
      }),
      finalize(() => { })
    );
  }

  getSharedCmaReports(params): Observable<any> {
    return this.cmaApi.getSharedCmaReport(params).pipe(
      startWith({ isLoading: true }),
      catchError((err) => {
        return of({ error: true, message: 'Failed to fetch reports', details: err });
      }),
      finalize(() => { })
    );
  }

  getCmaReportCount() {
    return this.cmaApi.getCmaReportCount().pipe(
      startWith({ isLoading: true }),
      catchError((err) => {
        return of({ error: true, message: 'Failed to fetch reports count', details: err });
      }),
      finalize(() => { })
    );
  }

  getCmaFetchComparables(params): Observable<any> {
    return this.cmaApi.getCmaFetchComparables(params).pipe(
      tap((res: any) => {
        this.cmaState.cmaFetchComparablesValue = res.data.comparables ?? null;
      }),
      startWith({ isLoading: true }),
      catchError((err) => {
        this.cmaState.cmaFetchComparablesValue = null;
        return of({ error: true, message: 'Failed to fetch report', details: err });
      }),
      finalize(() => { })
    );
  }


  saveSelectedComparables(params, type: string): Observable<{ isLoading?: boolean; data?: any }> {
    if (type != 'silent') {
      this.cmaState.isLoadingReport = true;
      this.cmaState.loadingMessage = 'Saving selected comparables...';
    }
    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.cmaApi
        .saveSelectedComparables(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            observer.next({ data: res?.data });
          },
          (err) => {
            console.log(err);
            this.cmaState.isLoadingReport = false;
            observer.next({ data: null });
          }
        );
    });
  }

  saveAdjustments(params): Observable<{ isLoading?: boolean; data?: any }> {
    this.cmaState.isLoadingReport = true;
    this.cmaState.loadingMessage = 'Saving adjustments...';

    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.cmaApi
        .saveAdjustments(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            observer.next({ data: res?.data });
          },
          (err) => {
            console.log(err);
            this.cmaState.isLoadingReport = false;
            observer.next({ data: null });
          }
        );
    });
  }

  saveAdjustedValueSelection(params): Observable<{ isLoading?: boolean; data?: any }> {

    return new Observable((observer) => {
      observer.next({ isLoading: true });
      this.cmaApi
        .saveAdjustedValueSelection(params)
        .pipe(
          finalize(() => {
            observer.next({ isLoading: false });
            observer.complete();
          })
        )
        .subscribe(
          (res: any) => {
            observer.next({ data: res?.data });
          },
          (err) => {
            console.log(err);
            observer.next({ data: null });
          }
        );
    });
  }

  getSmartReportCount() {
    return this.cmaApi.getSmartReportCount().pipe(
      startWith({ isLoading: true }),
      catchError((err) => {
        return of({ error: true, message: 'Failed to fetch reports count', details: err });
      }),
      finalize(() => { })
    );
  }
}
